import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Sparkles,
  RefreshCw,
  Palette,
  Check,
  Moon,
  Sun,
  ChevronDown,
  Send,
  MessageCircle,
  Pipette,
  Type,
  ArrowRight,
} from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import BrandSymbol from '@/components/BrandSymbol';
import { BRAND_SYMBOL_CATALOG } from '../../../../../sites/lucide-icon-catalog';
import type { WizardState } from './useWizardState';

interface StepProps {
  state: WizardState;
  updateState: (updates: Partial<WizardState>) => void;
}

const FONT_PAIRINGS = [
  // Classic
  { id: 'inter-playfair', heading: 'Playfair Display', body: 'Inter', label: 'Inter + Playfair Display', style: 'Classic' },
  { id: 'raleway-libre', heading: 'Libre Baskerville', body: 'Raleway', label: 'Raleway + Libre Baskerville', style: 'Classic' },
  { id: 'open-sans-playfair', heading: 'Playfair Display', body: 'Open Sans', label: 'Open Sans + Playfair Display', style: 'Classic' },
  // Modern
  { id: 'dm-sans-dm-serif', heading: 'DM Serif Display', body: 'DM Sans', label: 'DM Sans + DM Serif Display', style: 'Modern' },
  { id: 'poppins-merriweather', heading: 'Merriweather', body: 'Poppins', label: 'Poppins + Merriweather', style: 'Modern' },
  { id: 'montserrat-roboto-slab', heading: 'Roboto Slab', body: 'Montserrat', label: 'Montserrat + Roboto Slab', style: 'Modern' },
  { id: 'space-grotesk-spectral', heading: 'Spectral', body: 'Space Grotesk', label: 'Space Grotesk + Spectral', style: 'Modern' },
  // Friendly
  { id: 'nunito-lora', heading: 'Lora', body: 'Nunito', label: 'Nunito + Lora', style: 'Friendly' },
  { id: 'work-sans-bitter', heading: 'Bitter', body: 'Work Sans', label: 'Work Sans + Bitter', style: 'Friendly' },
  { id: 'rubik-karla', heading: 'Rubik', body: 'Karla', label: 'Rubik + Karla', style: 'Friendly' },
  // Professional
  { id: 'source-sans-source-serif', heading: 'Source Serif 4', body: 'Source Sans 3', label: 'Source Sans 3 + Source Serif 4', style: 'Professional' },
  { id: 'manrope-cormorant', heading: 'Cormorant', body: 'Manrope', label: 'Manrope + Cormorant', style: 'Professional' },
  { id: 'cabin-crimson-text', heading: 'Crimson Text', body: 'Cabin', label: 'Cabin + Crimson Text', style: 'Professional' },
  // Creative
  { id: 'outfit-fraunces', heading: 'Fraunces', body: 'Outfit', label: 'Outfit + Fraunces', style: 'Creative' },
  { id: 'josefin-sans-crimson-pro', heading: 'Crimson Pro', body: 'Josefin Sans', label: 'Josefin Sans + Crimson Pro', style: 'Creative' },
  { id: 'plus-jakarta-instrument-serif', heading: 'Instrument Serif', body: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans + Instrument Serif', style: 'Creative' },
];

// ---------------------------------------------------------------------------
// Google Fonts loader — dynamically injects <link> tags for font previews
// ---------------------------------------------------------------------------

const loadedFonts = new Set<string>();

function loadGoogleFonts(fontNames: string[]) {
  const toLoad = fontNames.filter((f) => !loadedFonts.has(f));
  if (toLoad.length === 0) return;
  toLoad.forEach((f) => loadedFonts.add(f));
  const families = toLoad.map((f) => `family=${f.replace(/ /g, '+')}:wght@400;700`).join('&');
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
  document.head.appendChild(link);
}

// ---------------------------------------------------------------------------
// Shared helper components
// ---------------------------------------------------------------------------

function AiMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-4 h-4 text-purple-600" />
      </div>
      <div className="flex-1 bg-stone-50 rounded-xl rounded-tl-sm p-4 text-sm text-text-secondary">
        {children}
      </div>
    </div>
  );
}

function UserMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-3 justify-end">
      <div className="bg-green-50 border border-green-100 rounded-xl rounded-tr-sm p-4 text-sm text-text-secondary max-w-[85%]">
        {children}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-4 h-4 text-purple-600" />
      </div>
      <div className="flex-1 bg-stone-50 rounded-xl rounded-tl-sm p-4">
        <div className="flex gap-1.5">
          <span className="w-2 h-2 rounded-full bg-stone-300 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 rounded-full bg-stone-300 animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 rounded-full bg-stone-300 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

function OptionCard({
  icon,
  label,
  description,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  description?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-start gap-3 text-left px-4 py-3 rounded-xl border-2 transition-all',
        'hover:border-purple-300 hover:bg-purple-50/50 hover:shadow-sm',
        'border-stone-200 bg-white',
        'disabled:opacity-50 disabled:pointer-events-none',
      )}
    >
      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        {icon}
      </div>
      <div>
        <div className="text-sm font-medium text-text-primary">{label}</div>
        {description && <div className="text-xs text-text-muted mt-0.5">{description}</div>}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Palette display helpers
// ---------------------------------------------------------------------------

function PalettePreview({ palette }: { palette: any }) {
  const [showDetails, setShowDetails] = useState(false);
  if (!palette) return null;

  const keyColors = [
    { label: 'Primary', hex: palette.primary?.['500'] || palette.primary?.base },
    { label: 'Secondary', hex: palette.secondary?.['500'] || palette.secondary?.base },
    { label: 'Accent', hex: palette.accent?.base || palette.accent?.['500'] },
  ].filter(c => c.hex);

  return (
    <div className="space-y-3">
      {/* Simplified: large key color swatches */}
      <div className="flex gap-4">
        {keyColors.map(({ label, hex }) => (
          <div key={label} className="flex items-center gap-2">
            <div
              className="w-10 h-10 rounded-lg border border-stone-200 shadow-sm"
              style={{ backgroundColor: hex }}
              title={hex}
            />
            <div>
              <div className="text-xs font-medium text-text-primary">{label}</div>
              <div className="text-xs text-text-muted font-mono">{hex}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Expandable full palette details */}
      <button
        type="button"
        onClick={() => setShowDetails(!showDetails)}
        className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
      >
        {showDetails ? 'Hide' : 'Show'} full palette details
        <ChevronDown className={cn('w-3 h-3 transition-transform', showDetails && 'rotate-180')} />
      </button>

      {showDetails && (
        <div className="space-y-2 pt-1">
          {palette.primary && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Primary scale</div>
              <div className="flex gap-0.5">
                {Object.entries(palette.primary).map(([key, hex]) => (
                  <div key={key} className="w-6 h-6 rounded border border-gray-200" style={{ backgroundColor: hex as string }} title={`${key}: ${hex}`} />
                ))}
              </div>
            </div>
          )}
          {palette.secondary && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Secondary scale</div>
              <div className="flex gap-0.5">
                {Object.entries(palette.secondary).map(([key, hex]) => (
                  <div key={key} className="w-6 h-6 rounded border border-gray-200" style={{ backgroundColor: hex as string }} title={`${key}: ${hex}`} />
                ))}
              </div>
            </div>
          )}
          {palette.accent && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Accent</div>
              <div className="flex gap-0.5">
                {Object.entries(palette.accent).map(([key, hex]) => (
                  <div key={key} className="w-6 h-6 rounded border border-gray-200" style={{ backgroundColor: hex as string }} title={`${key}: ${hex}`} />
                ))}
              </div>
            </div>
          )}
          {palette.text && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Text</div>
              <div className="flex gap-0.5">
                {Object.entries(palette.text).map(([key, hex]) => (
                  <div key={key} className="w-6 h-6 rounded border border-gray-200" style={{ backgroundColor: hex as string }} title={`${key}: ${hex}`} />
                ))}
              </div>
            </div>
          )}
          {palette.bg && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Background</div>
              <div className="flex gap-0.5">
                {Object.entries(palette.bg).map(([key, hex]) => (
                  <div key={key} className="w-6 h-6 rounded border border-gray-200" style={{ backgroundColor: hex as string }} title={`${key}: ${hex}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MiniSiteMockup({ palette, darkMode }: { palette: any; darkMode: boolean }) {
  const primary = palette?.primary?.['500'] || palette?.primary?.base || '#4a7c59';
  const accent = palette?.accent?.base || palette?.accent?.['500'] || '#c4704b';
  const bgColor = darkMode
    ? (palette?.bg?.dark || '#1a1a1a')
    : (palette?.bg?.light || palette?.bg?.base || '#ffffff');
  const textColor = darkMode
    ? (palette?.text?.light || '#e5e5e5')
    : (palette?.text?.primary || palette?.text?.dark || '#1a1a1a');
  const cardBg = darkMode ? '#2a2a2a' : '#f9fafb';

  return (
    <div
      className="rounded-lg border overflow-hidden"
      style={{ backgroundColor: bgColor, borderColor: darkMode ? '#333' : '#e5e7eb' }}
    >
      {/* Header bar */}
      <div className="h-10 flex items-center px-4" style={{ backgroundColor: primary }}>
        <div className="w-20 h-3 rounded bg-white/30" />
        <div className="ml-auto flex gap-3">
          <div className="w-10 h-2 rounded bg-white/20" />
          <div className="w-10 h-2 rounded bg-white/20" />
          <div className="w-10 h-2 rounded bg-white/20" />
        </div>
      </div>
      {/* Body */}
      <div className="p-4 space-y-3">
        <div className="text-center space-y-2">
          <div className="h-3 w-40 mx-auto rounded" style={{ backgroundColor: textColor, opacity: 0.8 }} />
          <div className="h-2 w-56 mx-auto rounded" style={{ backgroundColor: textColor, opacity: 0.4 }} />
          <button
            className="mt-2 px-4 py-1.5 rounded text-white text-xs font-medium"
            style={{ backgroundColor: accent }}
          >
            Get Started
          </button>
        </div>
        {/* Card */}
        <div className="rounded-lg p-3" style={{ backgroundColor: cardBg, border: `1px solid ${darkMode ? '#444' : '#e5e7eb'}` }}>
          <div className="h-2 w-24 rounded mb-2" style={{ backgroundColor: textColor, opacity: 0.6 }} />
          <div className="space-y-1">
            <div className="h-1.5 w-full rounded" style={{ backgroundColor: textColor, opacity: 0.2 }} />
            <div className="h-1.5 w-3/4 rounded" style={{ backgroundColor: textColor, opacity: 0.2 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat message type
// ---------------------------------------------------------------------------

interface PaletteOption {
  palettes: any;
  dark: any;
  fonts: { heading: string; body: string };
  explanation: string;
  fontExplanation: string;
  label: string;
}

interface ChatMessage {
  role: 'ai' | 'user';
  content: string;
  palette?: any;
  paletteOptions?: PaletteOption[];
  fonts?: { heading: string; body: string } | null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function WizardStep2Branding({ state, updateState }: StepProps) {
  // Color conversation state
  const [colorMessages, setColorMessages] = useState<ChatMessage[]>([]);
  const [colorChatInput, setColorChatInput] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [pickerColor, setPickerColor] = useState('#4a7c59');
  const [isGenerating, setIsGenerating] = useState(false);
  const [colorAccepted, setColorAccepted] = useState(false);
  const [showColorOptions, setShowColorOptions] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);
  const [, setPaletteOptions] = useState<PaletteOption[]>([]);

  // Font conversation state
  const [fontExplanation, setFontExplanation] = useState('');
  const [fontAccepted, setFontAccepted] = useState(false);
  const [showFontOptions, setShowFontOptions] = useState(false);
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  // The font-pairing id anchored as "slot 0" in the recommendation row.
  // Captured the first time the option cards appear (or whenever the AI
  // re-recommends) and held stable across the user's clicks so picking
  // card #3 doesn't shuffle it into slot #1. Without this anchor,
  // getFontRecommendations() reads state.fontPairingId on every render and
  // always puts the live selection first, which makes the cards jump.
  const [anchorFontId, setAnchorFontId] = useState<string | null>(null);

  // Shared state
  const [previewDark, setPreviewDark] = useState(false);
  const [currentPalette, setCurrentPalette] = useState<any>(null);
  const [currentFonts, setCurrentFonts] = useState<{ heading: string; body: string } | null>(null);
  void currentFonts; // used indirectly via setCurrentFonts

  const hasFetched = useRef(false);
  const chatInputRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const colorActionsRef = useRef<HTMLDivElement>(null);
  const suppressChatScroll = useRef(false);

  const selectedFont = FONT_PAIRINGS.find((fp) => fp.id === (state.fontPairingId || 'inter-playfair'))
    || FONT_PAIRINGS[0];

  // Scroll to bottom of chat when messages change (unless suppressed by palette selection)
  useEffect(() => {
    if (suppressChatScroll.current) {
      suppressChatScroll.current = false;
      return;
    }
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [colorMessages, isGenerating]);

  // Auto-fetch AI palette on mount
  useEffect(() => {
    if (hasFetched.current || !state.businessDescription) return;
    hasFetched.current = true;
    fetchInitialPalette();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.businessDescription]);

  // -------------------------------------------------------------------------
  // API calls
  // -------------------------------------------------------------------------

  const fetchInitialPalette = useCallback(async () => {
    setIsGenerating(true);
    try {
      const payload = { description: state.businessDescription || state.storeName || '', count: 3 };
      const response = await api.post<any>('/admin/ai/generate/theme', payload);

      // Handle multi-option response
      if (response.options && response.options.length > 1) {
        const options: PaletteOption[] = response.options;
        setPaletteOptions(options);
        setCurrentPalette(options[0].palettes);
        setCurrentFonts(options[0].fonts);

        suppressChatScroll.current = true;
        setColorMessages([
          {
            role: 'ai',
            content: `I've created ${options.length} palette options for your brand. Each takes a different design direction — pick the one that feels right, or we can refine from there.`,
            paletteOptions: options,
          },
        ]);
        setShowColorOptions(false); // Options shown inline via palette cards

        // Use first option's font recommendation
        applyFontRecommendation(options[0].fonts, response.options[0].fontExplanation);
        return;
      }

      // Single option fallback
      const palette = response.palettes || response.palette || null;
      const explanation = response.explanation || '';
      const fonts = response.fonts || null;

      setCurrentPalette(palette);
      setCurrentFonts(fonts);
      setPaletteOptions([]);
      if (palette) updateState({ palettes: palette });

      suppressChatScroll.current = true;
      setColorMessages([
        {
          role: 'ai',
          content: explanation || `Based on your business description, I've put together a color palette that should work well for your brand. Here's what I came up with:`,
          palette,
          fonts,
        },
      ]);
      setShowColorOptions(true);
      applyFontRecommendation(fonts, response.fontExplanation);
    } catch {
      setColorMessages([
        {
          role: 'ai',
          content: 'I ran into an issue generating a palette. You can try again or pick a primary color and I\'ll build from there.',
        },
      ]);
      setShowColorOptions(true);
      setFontExplanation('I wasn\'t able to generate a font recommendation, but you can choose from the pairings below.');
      setShowFontOptions(true);
    } finally {
      setIsGenerating(false);
    }
  }, [state.businessDescription, state.storeName, updateState]);

  function applyFontRecommendation(fonts: { heading: string; body: string } | null, fontExplanationText?: string) {
    if (fonts) {
      const headingLower = fonts.heading.toLowerCase().trim();
      const bodyLower = fonts.body.toLowerCase().trim();
      const matchingPairing = FONT_PAIRINGS.find(
        (fp) =>
          fp.heading.toLowerCase() === headingLower &&
          fp.body.toLowerCase() === bodyLower,
      ) || FONT_PAIRINGS.find(
        (fp) =>
          fp.heading.toLowerCase() === headingLower ||
          fp.body.toLowerCase() === bodyLower,
      );
      if (matchingPairing) {
        updateState({ fontPairingId: matchingPairing.id });
        // Pin this AI recommendation as slot 0 in the recommendation row.
        // Subsequent user clicks on the alternatives won't reshuffle it.
        setAnchorFontId(matchingPairing.id);
      }
      setFontExplanation(
        fontExplanationText ||
          `I recommend **${fonts.heading}** for headings and **${fonts.body}** for body text — they pair well together and suit your brand's personality.`,
      );
    } else {
      setFontExplanation(
        `I recommend **Playfair Display** for headings — it has a classic, authoritative feel. For body text, **Inter** is one of the most readable screen fonts available.`,
      );
      // No AI match — anchor on the default so the cards still render in a
      // stable order on first show.
      setAnchorFontId((prev) => prev ?? (state.fontPairingId || 'inter-playfair'));
    }
    setShowFontOptions(true);
  }

  async function fetchPaletteFromChat(refinement: string) {
    setIsGenerating(true);
    setShowColorOptions(false);
    setShowChatInput(false);
    setShowColorPicker(false);

    // Detect if user is asking for multiple options / alternatives
    const wantsOptions = /\b(options|alternatives|choices|variations|different|compare|show me|give me)\b/i.test(refinement);

    try {
      const payload: any = {
        description: state.businessDescription || state.storeName || '',
        refinement,
      };
      if (currentPalette) payload.current_palette = currentPalette;
      if (wantsOptions) payload.count = 3;

      const response = await api.post<any>('/admin/ai/generate/theme', payload);

      // Handle multi-option response
      if (response.options && response.options.length > 1) {
        const options: PaletteOption[] = response.options;
        setPaletteOptions(options);
        setCurrentPalette(options[0].palettes);
        setCurrentFonts(options[0].fonts);

        setColorMessages((prev) => [
          ...prev,
          {
            role: 'ai',
            content: `Here are ${options.length} different directions based on your request. Pick the one that speaks to you, or tell me what to adjust.`,
            paletteOptions: options,
          },
        ]);
        setShowColorOptions(false);
        applyFontRecommendation(options[0].fonts, options[0].fontExplanation);
        return;
      }

      const palette = response.palettes || response.palette || null;
      const explanation = response.explanation || '';
      const fonts = response.fonts || null;

      setCurrentPalette(palette);
      if (palette) updateState({ palettes: palette });
      if (fonts) setCurrentFonts(fonts);

      setColorMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          content: explanation || 'Here\'s an updated palette based on your feedback:',
          palette,
          fonts,
        },
      ]);
      setShowColorOptions(true);
    } catch {
      setColorMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          content: 'Sorry, I had trouble generating that. Want to try describing what you\'re looking for differently?',
        },
      ]);
      setShowColorOptions(true);
      setShowChatInput(true);
    } finally {
      setIsGenerating(false);
    }
  }

  async function fetchPaletteFromColor(hex: string) {
    setIsGenerating(true);
    setShowColorOptions(false);
    setShowColorPicker(false);

    setColorMessages((prev) => [
      ...prev,
      { role: 'user', content: `Use ${hex} as my primary color.` },
    ]);

    try {
      const payload = {
        description: `generate a complete palette using ${hex} as the primary color for a ${state.businessDescription || state.storeName || 'business'}`,
      };

      const response = await api.post<any>('/admin/ai/generate/theme', payload);
      const palette = response.palettes || response.palette || null;
      const explanation = response.explanation || '';

      setCurrentPalette(palette);
      if (palette) updateState({ palettes: palette });

      setColorMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          content: explanation || `Great choice! I've built a complete palette around ${hex}. Here's what I came up with:`,
          palette,
        },
      ]);
      setShowColorOptions(true);
    } catch {
      setColorMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          content: 'I had trouble building a palette from that color. Want to try a different one?',
        },
      ]);
      setShowColorOptions(true);
      setShowColorPicker(true);
    } finally {
      setIsGenerating(false);
    }
  }

  // -------------------------------------------------------------------------
  // Color action handlers
  // -------------------------------------------------------------------------

  function handleSelectOption(option: PaletteOption) {
    setCurrentPalette(option.palettes);
    setCurrentFonts(option.fonts);
    setPaletteOptions([]);
    updateState({ palettes: option.palettes });

    // Suppress the chat auto-scroll — we scroll to colorActionsRef instead
    suppressChatScroll.current = true;

    // Show the selected palette with its explanation and action buttons
    setColorMessages((prev) => [
      ...prev,
      { role: 'user', content: `I like the "${option.label}" option.` },
      {
        role: 'ai',
        content: option.explanation || `Great choice! Here's the ${option.label} palette in detail:`,
        palette: option.palettes,
        fonts: option.fonts,
      },
    ]);
    setShowColorOptions(true);
    applyFontRecommendation(option.fonts, option.fontExplanation);

    // Scroll to the accept/refine action buttons after React renders them
    requestAnimationFrame(() => {
      colorActionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  function handleAcceptPalette() {
    if (!currentPalette) return;
    setColorAccepted(true);
    setShowColorOptions(false);
    setShowChatInput(false);
    setShowColorPicker(false);
    updateState({ palettes: currentPalette });
    setColorMessages((prev) => [
      ...prev,
      { role: 'user', content: 'Looks great, let\'s use it!' },
      { role: 'ai', content: 'Awesome! Your color palette is locked in. Let\'s move on to fonts.' },
    ]);
  }

  function handlePickColor() {
    setShowColorOptions(false);
    setShowChatInput(false);
    setShowColorPicker(true);
    setColorMessages((prev) => [
      ...prev,
      { role: 'user', content: 'I\'d like to pick a primary color and build from there.' },
      { role: 'ai', content: 'Sure! Pick a primary color below, and I\'ll generate a full palette around it.' },
    ]);
  }

  function handleOpenChat() {
    setShowColorOptions(false);
    setShowColorPicker(false);
    setShowChatInput(true);
    setColorMessages((prev) => [
      ...prev,
      { role: 'user', content: 'Let\'s chat about it.' },
      {
        role: 'ai',
        content: 'Of course! Tell me what you have in mind. For example: "I want something warmer", "Make it more corporate", "I love teal and gold", or anything else.',
      },
    ]);
    // Scroll to chat input after render
    requestAnimationFrame(() => {
      chatInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      chatInputRef.current?.querySelector('input')?.focus();
    });
  }

  function handleChatSend() {
    const msg = colorChatInput.trim();
    if (!msg) return;
    setColorMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setColorChatInput('');
    fetchPaletteFromChat(msg);
  }

  function handleColorPicked() {
    fetchPaletteFromColor(pickerColor);
  }

  // -------------------------------------------------------------------------
  // Font action handlers
  // -------------------------------------------------------------------------

  function handleAcceptFonts() {
    setFontAccepted(true);
    setShowFontOptions(false);
    setShowFontDropdown(false);
  }

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  /** Return 3 font pairings: the AI-anchored one + 2 from different categories.
   *  Order is pinned to `anchorFontId` (snapshotted when the cards first
   *  appear), NOT to the live `state.fontPairingId`, so clicking a card
   *  highlights it in place instead of swapping positions. */
  function getFontRecommendations() {
    const anchorId = anchorFontId || state.fontPairingId || 'inter-playfair';
    const current = FONT_PAIRINGS.find((fp) => fp.id === anchorId) || FONT_PAIRINGS[0];
    const currentStyle = current.style;

    // Pick 2 alternatives from different categories
    const otherStyles = ['Classic', 'Modern', 'Friendly', 'Professional', 'Creative']
      .filter((s) => s !== currentStyle);
    const alternatives: typeof FONT_PAIRINGS = [];
    for (const style of otherStyles) {
      if (alternatives.length >= 2) break;
      const candidates = FONT_PAIRINGS.filter((fp) => fp.style === style && fp.id !== anchorId);
      if (candidates.length > 0) {
        alternatives.push(candidates[0]);
      }
    }

    const results = [current, ...alternatives];
    // Ensure all preview fonts are loaded from Google Fonts
    loadGoogleFonts(results.flatMap((fp) => [fp.heading, fp.body]));
    return results;
  }

  function renderMarkdownBold(text: string) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return <span key={i}>{part}</span>;
    });
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* ================================================================= */}
      {/* COLOR SECTION                                                      */}
      {/* ================================================================= */}
      <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-purple-500" />
            <h3 className="font-semibold text-text-primary">Color Palette</h3>
          </div>
          <button
            onClick={() => setPreviewDark(!previewDark)}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
            title={previewDark ? 'Show light mode' : 'Show dark mode'}
          >
            {previewDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>

        {/* Chat-style conversation */}
        <div className="space-y-4">
          {colorMessages.map((msg, i) => {
            if (msg.role === 'user') {
              return <UserMessage key={i}>{msg.content}</UserMessage>;
            }
            return (
              <AiMessage key={i}>
                <p className="mb-0">{msg.content}</p>
                {msg.paletteOptions && msg.paletteOptions.length > 1 && (
                  <div className="mt-4 grid gap-3 sm:grid-cols-1 lg:grid-cols-3">
                    {msg.paletteOptions.map((opt, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSelectOption(opt)}
                        className={cn(
                          'text-left rounded-xl border-2 overflow-hidden transition-all',
                          'hover:border-purple-400 hover:shadow-md',
                          'border-stone-200 bg-white',
                        )}
                      >
                        <MiniSiteMockup palette={opt.palettes} darkMode={previewDark} />
                        <div className="p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-purple-600">{opt.label}</span>
                          </div>
                          <div className="flex gap-1.5">
                            {[opt.palettes.primary?.['500'], opt.palettes.secondary?.['500'], opt.palettes.accent?.base].filter(Boolean).map((hex, ci) => (
                              <div key={ci} className="w-6 h-6 rounded-full border border-stone-200" style={{ backgroundColor: hex }} />
                            ))}
                          </div>
                          <p className="text-xs text-text-muted line-clamp-2">{opt.explanation}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {msg.palette && (
                  <div className="mt-3 space-y-3">
                    <div className="p-3 bg-white rounded-lg border border-stone-200">
                      <PalettePreview palette={previewDark && msg.palette.dark ? msg.palette.dark : msg.palette} />
                    </div>
                    <MiniSiteMockup palette={msg.palette} darkMode={previewDark} />
                  </div>
                )}
              </AiMessage>
            );
          })}

          {/* Typing indicator while generating */}
          {isGenerating && <TypingIndicator />}

          {/* Option cards — accept / refine / chat */}
          {showColorOptions && !isGenerating && !colorAccepted && currentPalette && (
            <div className="ml-11" ref={colorActionsRef}>
              <p className="text-xs text-text-muted mb-2 font-medium">What would you like to do?</p>
              <div className="grid gap-2 sm:grid-cols-1">
                <OptionCard
                  icon={<Check className="w-4 h-4 text-green-600" />}
                  label="Looks great, let's use it!"
                  description="Accept this palette and move on"
                  onClick={handleAcceptPalette}
                />
                <OptionCard
                  icon={<Pipette className="w-4 h-4 text-purple-600" />}
                  label="Pick a primary color and build from there"
                  description="Choose your own base color"
                  onClick={handlePickColor}
                />
                <OptionCard
                  icon={<MessageCircle className="w-4 h-4 text-purple-600" />}
                  label="Let's chat about it"
                  description="Tell me what you'd like to change"
                  onClick={handleOpenChat}
                />
              </div>
            </div>
          )}

          {/* No palette fallback options */}
          {showColorOptions && !isGenerating && !colorAccepted && !currentPalette && (
            <div className="ml-11">
              <div className="grid gap-2 sm:grid-cols-1">
                <OptionCard
                  icon={<RefreshCw className="w-4 h-4 text-purple-600" />}
                  label="Try generating again"
                  onClick={() => fetchInitialPalette()}
                  disabled={!state.businessDescription}
                />
                <OptionCard
                  icon={<Pipette className="w-4 h-4 text-purple-600" />}
                  label="Pick a primary color and build from there"
                  onClick={handlePickColor}
                />
              </div>
            </div>
          )}

          {/* Color picker */}
          {showColorPicker && !isGenerating && (
            <div className="ml-11">
              <div className="flex items-end gap-3 p-4 bg-stone-50 rounded-xl border border-stone-200">
                <div>
                  <label className="block text-xs font-medium text-text-muted mb-1.5">
                    Pick your primary color
                  </label>
                  <input
                    type="color"
                    value={pickerColor}
                    onChange={(e) => setPickerColor(e.target.value)}
                    className="w-16 h-16 rounded-lg border-2 border-stone-300 cursor-pointer"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-text-muted mb-1.5">
                    Hex value
                  </label>
                  <input
                    type="text"
                    value={pickerColor}
                    onChange={(e) => setPickerColor(e.target.value)}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
                    placeholder="#4a7c59"
                  />
                </div>
                <Button variant="primary" onClick={handleColorPicked}>
                  <ArrowRight className="w-4 h-4" />
                  Build Palette
                </Button>
              </div>
            </div>
          )}

          {/* Chat input */}
          {showChatInput && !isGenerating && (
            <div ref={chatInputRef} className="ml-11">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={colorChatInput}
                  onChange={(e) => setColorChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleChatSend()}
                  placeholder="e.g. &quot;Make it warmer&quot;, &quot;I love teal&quot;, &quot;More corporate&quot;..."
                  className="flex-1 px-4 py-2.5 border border-stone-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
                />
                <Button
                  variant="primary"
                  onClick={handleChatSend}
                  disabled={!colorChatInput.trim()}
                  className="rounded-xl"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Accepted state */}
          {colorAccepted && (
            <div className="ml-11">
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <Check className="w-4 h-4" />
                <span>Palette accepted!</span>
                <button
                  type="button"
                  className="ml-auto text-xs text-green-600 hover:text-green-800 underline"
                  onClick={() => {
                    setColorAccepted(false);
                    setShowColorOptions(true);
                  }}
                >
                  Change
                </button>
              </div>
            </div>
          )}

          {/* Empty state */}
          {colorMessages.length === 0 && !isGenerating && (
            <div className="text-sm text-text-muted py-6 text-center">
              {state.businessDescription
                ? 'Generating your palette...'
                : 'Add a business description in Step 1 to get AI palette recommendations.'}
              {!state.businessDescription && (
                <div className="mt-3 flex justify-center">
                  <OptionCard
                    icon={<Pipette className="w-4 h-4 text-purple-600" />}
                    label="Pick a primary color manually"
                    onClick={() => {
                      setShowColorPicker(true);
                      setColorMessages([
                        {
                          role: 'ai',
                          content: 'No worries! Pick a primary color and I\'ll generate a full palette from it.',
                        },
                      ]);
                    }}
                  />
                </div>
              )}
            </div>
          )}

          <div ref={chatEndRef} />
        </div>
      </div>

      {/* ================================================================= */}
      {/* FONT SECTION                                                       */}
      {/* ================================================================= */}
      <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Type className="w-5 h-5 text-purple-500" />
          <h3 className="font-semibold text-text-primary">Font Pairing</h3>
        </div>

        {/* AI font recommendation */}
        {fontExplanation && (
          <AiMessage>
            <p>{renderMarkdownBold(fontExplanation)}</p>
          </AiMessage>
        )}

        {/* Font options — show 3 cards: AI-recommended + 2 alternatives */}
        {showFontOptions && !fontAccepted && (
          <>
            <div className="ml-11">
              <p className="text-xs text-text-muted mb-3 font-medium">Pick a font pairing:</p>
              <div className="grid gap-3 sm:grid-cols-1 lg:grid-cols-3">
                {getFontRecommendations().map((fp, idx) => {
                  const isSelected = (state.fontPairingId || 'inter-playfair') === fp.id;
                  return (
                    <button
                      key={fp.id}
                      type="button"
                      onClick={() => {
                        updateState({ fontPairingId: fp.id });
                        setFontExplanation(`Great choice! **${fp.heading}** paired with **${fp.body}** gives a ${fp.style.toLowerCase()} feel that should work nicely.`);
                      }}
                      className={cn(
                        'text-left rounded-xl border-2 p-4 transition-all space-y-3',
                        isSelected
                          ? 'border-purple-500 bg-purple-50/50 shadow-sm'
                          : 'border-stone-200 bg-white hover:border-purple-300 hover:shadow-sm',
                      )}
                    >
                      {idx === 0 && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                          AI Pick
                        </span>
                      )}
                      <h3
                        className="text-lg font-bold text-text-primary leading-tight"
                        style={{ fontFamily: `'${fp.heading}', serif` }}
                      >
                        {state.storeName || 'Your Store'}
                      </h3>
                      <p
                        className="text-xs text-text-secondary leading-relaxed"
                        style={{ fontFamily: `'${fp.body}', sans-serif` }}
                      >
                        Premium products crafted with care and delivered to your door.
                      </p>
                      <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                        <span className="text-[10px] text-text-muted">
                          {fp.heading} + {fp.body}
                        </span>
                        <span className="text-[10px] text-purple-500 font-medium">{fp.style}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="ml-11">
              <div className="grid gap-2 sm:grid-cols-2">
                <OptionCard
                  icon={<Check className="w-4 h-4 text-green-600" />}
                  label="Use selected fonts"
                  description={`${selectedFont.heading} + ${selectedFont.body}`}
                  onClick={handleAcceptFonts}
                />
                <OptionCard
                  icon={<ChevronDown className="w-4 h-4 text-purple-600" />}
                  label="Browse all 16 pairings"
                  description="Open full list by category"
                  onClick={() => setShowFontDropdown(!showFontDropdown)}
                />
              </div>
            </div>
          </>
        )}

        {/* Font dropdown (manual selection) */}
        {showFontDropdown && !fontAccepted && (
          <div className="ml-11">
            <div className="relative">
              <select
                className={cn(
                  'w-full px-3 py-2 border border-stone-300 rounded-lg text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500',
                  'appearance-none pr-10',
                )}
                value={state.fontPairingId || 'inter-playfair'}
                onChange={(e) => {
                  updateState({ fontPairingId: e.target.value });
                  const fp = FONT_PAIRINGS.find((f) => f.id === e.target.value);
                  if (fp) {
                    setFontExplanation(`Good pick! **${fp.heading}** paired with **${fp.body}** gives a ${fp.style.toLowerCase()} feel that should work nicely.`);
                  }
                }}
              >
                {['Classic', 'Modern', 'Friendly', 'Professional', 'Creative'].map((style) => (
                  <optgroup key={style} label={style}>
                    {FONT_PAIRINGS.filter((fp) => fp.style === style).map((fp) => (
                      <option key={fp.id} value={fp.id}>
                        {fp.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
            </div>
          </div>
        )}

        {/* Font accepted state */}
        {fontAccepted && (
          <div className="ml-11">
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <Check className="w-4 h-4" />
              <span>
                Fonts set: <strong>{selectedFont.heading}</strong> + <strong>{selectedFont.body}</strong>
              </span>
              <button
                type="button"
                className="ml-auto text-xs text-green-600 hover:text-green-800 underline"
                onClick={() => {
                  setFontAccepted(false);
                  setShowFontOptions(true);
                }}
              >
                Change
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ================================================================= */}
      {/* BRAND SYMBOL SECTION                                               */}
      {/* ================================================================= */}
      <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-500" />
          <h3 className="font-semibold text-text-primary">Brand Symbol</h3>
        </div>

        <AiMessage>
          <p className="mb-0">
            This icon shows up wherever a product image is missing — on cards, empty states, and search results. Pick one that fits the brand, or leave it on auto and we'll choose during launch.
          </p>
        </AiMessage>

        <div className="ml-11 space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-text-muted">Selected:</span>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-50 border border-purple-200">
              <BrandSymbol name={state.brandSymbol || 'Sparkles'} className="w-4 h-4 text-purple-700" />
              <span className="font-medium text-purple-700">
                {state.brandSymbol || 'Auto (AI will pick)'}
              </span>
            </div>
            {state.brandSymbol && (
              <button
                type="button"
                className="text-xs text-text-muted hover:text-text-primary underline"
                onClick={() => updateState({ brandSymbol: '' })}
              >
                Clear
              </button>
            )}
          </div>

          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2 max-h-72 overflow-y-auto p-2 border border-stone-200 rounded-lg">
            {BRAND_SYMBOL_CATALOG.map((name) => {
              const isSelected = state.brandSymbol === name;
              return (
                <button
                  key={name}
                  type="button"
                  title={name}
                  onClick={() => updateState({ brandSymbol: name })}
                  className={cn(
                    'aspect-square flex items-center justify-center rounded-md border transition-all',
                    isSelected
                      ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-sm'
                      : 'border-stone-200 bg-white text-text-secondary hover:border-purple-300 hover:bg-purple-50/50'
                  )}
                >
                  <BrandSymbol name={name} className="w-5 h-5" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
